import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useProducts } from "../../application/hooks/useProducts";
import { useExchangeRate } from "../../application/hooks/useExchangeRate";
import { useCart } from "../../application/store/cartStore";
import { useCharge } from "../../application/hooks/useCharge";
import { useDeviceSession } from "../../application/hooks/useDeviceSession";
import type { PaymentMethod } from "../../infrastructure/deviceApi";
import Cart from "../components/Cart";

export default function PosPage() {
  const { products, loading } = useProducts();
  const { rate } = useExchangeRate();
  const cartState = useCart();
  const { charge, charging, error: chargeError } = useCharge();
  const { cash, lightning } = useDeviceSession();
  const navigate = useNavigate();
  const [showCart, setShowCart] = useState(false);

  const satsPerMxn = rate ? 100_000_000 / rate.mxn_per_btc : null;

  const handleCheckout = async (
    method: PaymentMethod,
    tipMxn: number,
    discountMxn: number
  ) => {
    if (cartState.items.length === 0) return;
    const res = await charge(cartState.items, method, { tipMxn, discountMxn });
    if (!res) return;
    if (res.method === "cash") {
      // El efectivo no pasa por la pantalla de espera: el dinero ya esta en el cajon.
      cartState.clear();
      navigate(`/pos/confirmed?mxn=${res.order.total_mxn}&metodo=cash`, { replace: true });
      return;
    }
    if (res.invoice) navigate(`/pos/pay?invoice=${res.invoice.id}`);
  };

  return (
    <div className="flex h-full flex-col md:flex-row md:gap-4">
      {/* Sin esto el botón "Cobrar" era mudo: al fallar, `data` venía null y no pasaba nada. */}
      {chargeError && (
        <div
          data-testid="pos-charge-error"
          className="fixed inset-x-3 top-3 z-50 rounded-lg bg-error/15 px-4 py-3 text-sm text-error shadow-lg md:inset-x-auto md:right-3 md:max-w-md"
          role="alert"
        >
          {chargeError}
        </div>
      )}
      {/* Product grid */}
      <div className="flex-1 overflow-y-auto">
        <div className="mb-3 flex items-center justify-between">
          <h1 className="text-lg font-bold">POS</h1>
          {rate ? (
            <span className="font-mono text-xs text-text-secondary">
              1 BTC = ${rate.mxn_per_btc.toLocaleString()} MXN
            </span>
          ) : (
            <span className="text-xs text-warning">Sin tipo de cambio</span>
          )}
        </div>

        {loading ? (
          <p className="text-text-secondary">Cargando productos...</p>
        ) : products.length === 0 ? (
          <div className="mt-16 text-center">
            <p className="text-text-secondary">No hay productos.</p>
            <p className="text-sm text-text-secondary">
              Agrega productos en la seccion de Productos.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {products.map((p) => (
              <button
                key={p.id}
                onClick={() => cartState.add(p)}
                className="flex flex-col items-center justify-center rounded-lg border border-border-default bg-bg-surface p-4 text-center transition-colors hover:bg-bg-surface-hover active:border-accent"
              >
                <span className="mb-1 text-sm font-medium">{p.name}</span>
                <span className="font-mono text-accent">
                  ${p.price_mxn.toFixed(2)}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Cart — sidebar on tablet+, bottom sheet toggle on mobile */}
      <div className="hidden w-72 shrink-0 rounded-lg border border-border-default bg-bg-surface p-4 md:flex md:flex-col">
        <Cart
          items={cartState.items}
          subtotalMxn={cartState.totalMxn}
          satsPerMxn={satsPerMxn}
          onUpdateQuantity={cartState.updateQuantity}
          onRemove={cartState.remove}
          onCheckout={handleCheckout}
          onClear={cartState.clear}
          checkoutDisabled={charging !== null || cartState.items.length === 0}
          canLightning={lightning}
          canCash={cash}
          charging={charging}
        />
      </div>

      {/* Mobile: floating cart button + bottom sheet */}
      {cartState.count > 0 && !showCart && (
        <button
          onClick={() => setShowCart(true)}
          className="fixed bottom-20 right-4 z-40 flex items-center gap-2 rounded-full bg-accent px-5 py-3 font-bold text-bg-primary shadow-lg md:hidden"
        >
          Carrito ({cartState.count}) · ${cartState.totalMxn.toFixed(2)}
        </button>
      )}

      {showCart && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setShowCart(false)}
          />
          <div className="absolute inset-x-0 bottom-0 max-h-[70dvh] overflow-y-auto rounded-t-xl bg-bg-surface p-4">
            <Cart
              items={cartState.items}
              subtotalMxn={cartState.totalMxn}
              satsPerMxn={satsPerMxn}
              onUpdateQuantity={cartState.updateQuantity}
              onRemove={cartState.remove}
              onCheckout={(method, tipMxn, discountMxn) => {
                setShowCart(false);
                handleCheckout(method, tipMxn, discountMxn);
              }}
              onClear={cartState.clear}
              checkoutDisabled={charging !== null || cartState.items.length === 0}
              canLightning={lightning}
              canCash={cash}
              charging={charging}
            />
          </div>
        </div>
      )}
    </div>
  );
}
